import { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Scale,
  X,
  XCircle,
} from "lucide-react";
import {
  useVerifyProposalLegal,
  type DetectedCitation,
  type LegalVerificationResult,
} from "@/hooks/use-legal";

/**
 * Mejora N2 (changelog cliente): diálogo del verificador legal.
 *
 * Lanza el endpoint que escanea TODAS las secciones de la propuesta y
 * presenta el resultado clasificado por estado (vigente, derogada,
 * desconocida). Cada cita derogada o desconocida muestra la sugerencia
 * accionable que devuelve el backend para que el equipo sepa qué hacer.
 */

interface LegalVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  proposalTitle: string;
}

function citationCard(c: DetectedCitation, idx: number) {
  const statusStyles =
    c.status === "vigente"
      ? {
          bg: "bg-emerald-50",
          border: "border-emerald-200",
          color: "text-emerald-700",
          Icon: CheckCircle2,
          label: "Vigente",
        }
      : c.status === "derogada"
      ? {
          bg: "bg-red-50",
          border: "border-red-200",
          color: "text-red-700",
          Icon: XCircle,
          label: "Derogada",
        }
      : {
          bg: "bg-amber-50",
          border: "border-amber-200",
          color: "text-amber-800",
          Icon: AlertTriangle,
          label: "Desconocida",
        };

  return (
    <li
      key={`${c.matched_law_id ?? "unknown"}-${idx}`}
      className={`border ${statusStyles.border} ${statusStyles.bg} rounded-md p-3`}
    >
      <div className="flex items-start gap-2">
        <statusStyles.Icon
          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${statusStyles.color}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {c.matched_law_title ?? c.cited_text}
            </span>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusStyles.bg} ${statusStyles.color} border ${statusStyles.border}`}
            >
              {statusStyles.label}
            </span>
            {c.in_framework === false && (
              <span
                className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200"
                title="Norma válida, pero no pertenece al marco legal de esta licitación"
              >
                Fuera de marco
              </span>
            )}
            {c.section_title && (
              <span className="text-[11px] text-gray-500">
                → {c.section_title}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-600 mt-1 italic">
            “{c.cited_text}”
          </p>
          {c.superseded_by_title && (
            <p className="text-xs text-gray-700 mt-1">
              Sustituida por{" "}
              <span className="font-medium">{c.superseded_by_title}</span>
            </p>
          )}
          {c.suggestion && (
            <p className="text-xs text-gray-700 mt-1">{c.suggestion}</p>
          )}
        </div>
      </div>
    </li>
  );
}

function SummaryBar({ result }: { result: LegalVerificationResult }) {
  const { summary } = result;
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-center">
        <div className="text-2xl font-bold text-emerald-700">
          {summary.vigente_count}
        </div>
        <div className="text-xs text-emerald-700">vigentes</div>
      </div>
      <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center">
        <div className="text-2xl font-bold text-red-700">
          {summary.derogada_count}
        </div>
        <div className="text-xs text-red-700">derogadas</div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center">
        <div className="text-2xl font-bold text-amber-700">
          {summary.desconocida_count}
        </div>
        <div className="text-xs text-amber-700">desconocidas</div>
      </div>
    </div>
  );
}

/**
 * Fase 3 (marco legal por licitación): chequeo de completitud. Lista las
 * normas del marco de la licitación que NO se citan en ninguna sección.
 */
function CompletenessSection({ result }: { result: LegalVerificationResult }) {
  if (!result.framework_scoped) return null;
  const uncited = result.uncited_framework_norms ?? [];

  if (uncited.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800">
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        La propuesta cita todas las normas de su marco legal.
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-1">
        Normas del marco no citadas ({uncited.length})
      </h3>
      <p className="text-xs text-gray-500 mb-2">
        Aplican a esta licitación (su marco legal) pero no aparecen citadas en
        ninguna sección. Revisa si deberían mencionarse.
      </p>
      <ul className="space-y-1.5">
        {uncited.map((n) => (
          <li
            key={n.law_id}
            className="flex items-start gap-2 border border-gray-200 bg-gray-50 rounded-md p-2"
          >
            <Scale className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-sm font-medium text-gray-800">
                {n.short_title}
              </span>
              <span className="text-xs text-gray-500"> — {n.title}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LegalVerificationDialog({
  isOpen,
  onClose,
  proposalId,
  proposalTitle,
}: LegalVerificationDialogProps) {
  const verifyMutation = useVerifyProposalLegal();
  const [lastResult, setLastResult] = useState<LegalVerificationResult | null>(
    null
  );

  // Lanzamos la verificación al abrir el diálogo si todavía no hay
  // resultado para esta propuesta. Si el usuario cierra y vuelve a abrir,
  // el resultado previo se mantiene hasta que pulse "Reverificar" — no
  // relanzamos solo, cuesta tokens.
  //
  // No metemos `verifyMutation` en deps porque useMutation devuelve un
  // objeto nuevo en cada render (mismo patrón que ya usamos en otros
  // diálogos del proyecto para evitar bucles infinitos).
  const verifyMutate = verifyMutation.mutate;
  useEffect(() => {
    if (!isOpen) return;
    if (lastResult) return;
    verifyMutate(
      { proposalId },
      { onSuccess: (res) => setLastResult(res) }
    );
  }, [isOpen, proposalId, lastResult, verifyMutate]);

  const handleClose = () => {
    onClose();
  };

  const handleRerun = () => {
    verifyMutation.mutate(
      { proposalId },
      { onSuccess: (res) => setLastResult(res) }
    );
  };

  if (!isOpen) return null;

  const result = lastResult;
  const hasIssues =
    result &&
    (result.summary.derogada_count > 0 || result.summary.desconocida_count > 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Verificación de marco legal de la propuesta"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Verificación legal
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Propuesta:{" "}
            <span className="font-medium text-gray-900">{proposalTitle}</span>
          </p>

          {verifyMutation.isPending && (
            <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
              <Loader2 className="w-4 h-4 animate-spin" />
              Escaneando todas las secciones contra el catálogo legal…
            </div>
          )}

          {verifyMutation.isError && !result && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>
                Error al verificar el marco legal. Pulsa{" "}
                <em>Reintentar</em> abajo.
              </span>
            </div>
          )}

          {result && (
            <>
              <SummaryBar result={result} />

              {result.summary.total_citations === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                  <AlertCircle className="w-4 h-4" />
                  El LLM no detectó referencias normativas en las secciones
                  actuales. Si esperabas alguna, prueba reverificar.
                </div>
              ) : !hasIssues ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" />
                  Todas las citas legales detectadas son vigentes según el
                  catálogo {result.catalog_version}.
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <span>
                    Hay citas que necesitan revisión antes de presentar. Mira
                    las sugerencias de cada una abajo.
                  </span>
                </div>
              )}

              {result.citations.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Referencias detectadas ({result.citations.length})
                    {result.summary.out_of_framework_count > 0 && (
                      <span className="ml-2 font-normal text-blue-700">
                        · {result.summary.out_of_framework_count} fuera de marco
                      </span>
                    )}
                  </h3>
                  <ul className="space-y-2">
                    {result.citations.map((c, idx) => citationCard(c, idx))}
                  </ul>
                </div>
              )}

              <CompletenessSection result={result} />

              {result.framework_scoped && (
                <p className="text-[11px] text-gray-400">
                  Verificación acotada al marco legal de esta licitación. Gestiona
                  el marco en la pestaña{" "}
                  <span className="font-medium text-gray-500">Marco legal</span>{" "}
                  de la licitación.
                </p>
              )}

              <p className="text-[11px] text-gray-400 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Catálogo {result.catalog_version} — fuente: BOE oficial. Para
                consultar el catálogo completo ve a{" "}
                <a
                  href="/configuracion/marco-legal"
                  className="text-blue-600 hover:underline"
                >
                  Configuración → Marco legal
                </a>
                .
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={handleRerun}
            disabled={verifyMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {verifyMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verificando…
              </>
            ) : verifyMutation.isError && !lastResult ? (
              <>
                <Scale className="w-4 h-4" />
                Reintentar
              </>
            ) : (
              <>
                <Scale className="w-4 h-4" />
                Reverificar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LegalVerificationDialog;
